import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import { getFreshBatchStatus } from "./freshBatchStatus";
import "./dashboard.css";
import "./batchWorkspace.css";
import "./freshStock.css";

interface FreshParent {
  id: string;
  name_sv: string;
  name_en: string;
  componentProductIds?: string[];
}

interface FreshPackage {
  firebaseId: string;
  id: string;
  parentId: string;
  size: number;
  formatedSize: string;
  isBox?: boolean;
  active?: boolean;
  boundBagPackageId?: string;
}

interface FreshProductBatch {
  firebaseId: string;
  productId: string;
  batchId: string;
  plantDate: number | null;
  plantedBy: string;
  harvestDate: number | null;
  harvestedBy: string;
  trayCount: number | null;
  freshWeight: number | null;
  createdAt: number;
  canceled?: boolean;
  exception?: boolean;
}

interface PackageBatch {
  firebaseId: string;
  packageId: string;
  sourceBatchRef: string;
  sourceBatchId: string;
  sourceProductId?: string;
  batchId: string;
  quantity?: number;
  reservedQuantity?: number;
  boxPackageId?: string;
  boxQuantity?: number;
  boxMultiplier?: number;
  createdAt: number;
}

interface MultiStockRow {
  id: string;
  product: FreshParent | null;
  sourceBatch: FreshProductBatch | null;
  package: FreshPackage | null;
  quantity: number;
}

type StockFilter = "all" | "week" | "available" | "reserved" | "unavailable";
const STOCK_FILTERS: Array<{ value: StockFilter; label: string }> = [
  { value: "all", label: "Alla" }, { value: "week", label: "Veckans skörd" },
  { value: "available", label: "Ledigt" }, { value: "reserved", label: "Reserverat" },
  { value: "unavailable", label: "Inget ledigt" },
];
const stockCountLabel = (count: number) => `${count} ${count === 1 ? "lagerpost" : "lagerposter"}`;
const quantityOf = (entry: PackageBatch) => entry.quantity ?? 1;
const reservedOf = (entry: PackageBatch) => entry.reservedQuantity ?? 0;
const availableOf = (entry: PackageBatch) => Math.max(0, quantityOf(entry) - reservedOf(entry));

function StockDialog({ id, open, title, description, onClose, busy, error, wide, children, actions }: {
  id: string; open: boolean; title: string; description: string; onClose: () => void;
  busy?: boolean; error?: string; wide?: boolean; children: React.ReactNode; actions: React.ReactNode;
}) {
  return <Dialog className="FreshDialog BatchDialog StockDialog" fullWidth maxWidth={wide ? "md" : "sm"}
    open={open} onClose={busy ? undefined : onClose} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}>
    <DialogTitle component="div"><div><h2 id={`${id}-title`}>{title}</h2><p className="BatchDialogSubtitle" id={`${id}-description`}>{description}</p></div><IconButton aria-label="Stäng" onClick={onClose} disabled={busy}><CloseIcon /></IconButton></DialogTitle>
    <DialogContent>{children}</DialogContent>
    {error && <Alert severity="error" className="BatchSaveError">{error}</Alert>}
    <DialogActions>{actions}</DialogActions>
  </Dialog>;
}

function StockFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <fieldset className="BatchFormSection"><legend>{title}</legend>{children}</fieldset>;
}

function DashBoardFreshStock() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();

  // --- List state ---
  const [packageBatches, setPackageBatches] = useState<PackageBatch[]>([]);
  const [packages, setPackages] = useState<FreshPackage[]>([]);
  const [parents, setParents] = useState<FreshParent[]>([]);
  const [freshBatches, setFreshBatches] = useState<FreshProductBatch[]>([]);
  const [loadingCollections, setLoadingCollections] = useState({ products: true, packages: true, batches: true, stock: true });
  const firebaseLoading = Object.values(loadingCollections).some(Boolean);
  const [loadErrors, setLoadErrors] = useState<Partial<Record<keyof typeof loadingCollections, string>>>({});
  const [reload, setReload] = useState(0);
  const [sortBy, setSortBy] = useState<"package" | "batch" | "created">("created");
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const [singleError, setSingleError] = useState("");
  const [multiError, setMultiError] = useState("");
  const [deletingEntry, setDeletingEntry] = useState<PackageBatch | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // --- Info dialog ---
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // --- Single batch edit dialog ---
  const [open, setOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PackageBatch | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  // --- Multi-batch add dialog ---
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiDialogLoading, setMultiDialogLoading] = useState(false);

  // Form fields for single add/edit
  const [singleProduct, setSingleProduct] = useState<FreshParent | null>(null);
  const [singlePackage, setSinglePackage] = useState<FreshPackage | null>(null);
  const [singleSourceBatches, setSingleSourceBatches] = useState<FreshProductBatch[]>([]);
  const [singleQuantity, setSingleQuantity] = useState(1);

  // Form fields for multi-lager add
  const [multiRows, setMultiRows] = useState<MultiStockRow[]>([]);

  // Listen to products_fresh for parent names
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh"), orderBy("name_sv"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreshParent[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        temp.push({
          id: doc.id,
          name_sv: data.name_sv || "",
          name_en: data.name_en || "",
          componentProductIds: Array.isArray(data.componentProductIds) ? data.componentProductIds : [],
        });
      });
      setParents(temp);
      setLoadingCollections((prev) => ({ ...prev, products: false }));
    }, (error) => {
      console.error("Error loading products:", error);
      setLoadErrors((prev) => ({ ...prev, products: "Det gick inte att hämta produkter." }));
      setLoadingCollections((prev) => ({ ...prev, products: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  // Listen to packages
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh_package"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreshPackage[] = [];
      snapshot.forEach((doc) => { temp.push({ firebaseId: doc.id, ...doc.data() } as FreshPackage); });
      setPackages(temp);
      setLoadingCollections((prev) => ({ ...prev, packages: false }));
    }, (error) => {
      console.error("Error loading packages:", error);
      setLoadErrors((prev) => ({ ...prev, packages: "Det gick inte att hämta förpackningar." }));
      setLoadingCollections((prev) => ({ ...prev, packages: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  // Listen to fresh batches
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh_batch"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreshProductBatch[] = [];
      snapshot.forEach((doc) => { temp.push({ firebaseId: doc.id, ...doc.data() } as FreshProductBatch); });
      setFreshBatches(temp);
      setLoadingCollections((prev) => ({ ...prev, batches: false }));
    }, (error) => {
      console.error("Error loading batches:", error);
      setLoadErrors((prev) => ({ ...prev, batches: "Det gick inte att hämta odlingsbatcher." }));
      setLoadingCollections((prev) => ({ ...prev, batches: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  // Listen to package batches
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh_package_batch"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: PackageBatch[] = [];
      snapshot.forEach((doc) => { temp.push({ firebaseId: doc.id, ...doc.data() } as PackageBatch); });
      setPackageBatches(temp);
      setLoadingCollections((prev) => ({ ...prev, stock: false }));
    }, (error) => {
      console.error("Error loading stock:", error);
      setLoadErrors((prev) => ({ ...prev, stock: "Det gick inte att hämta lagerposter." }));
      setLoadingCollections((prev) => ({ ...prev, stock: false }));
    });
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => { if (user === null && loading === false) navigate("/login"); }, [user, loading, navigate]);

  // --- Helpers ---

  const getPackageLabel = (pkgId: string): string => {
    const pkg = packages.find((p) => p.id === pkgId);
    if (!pkg) return pkgId;
    const parent = parents.find((p) => p.id === pkg.parentId);
    const productName = parent ? `${parent.name_sv}` : pkg.parentId;
    return `${productName} — ${pkg.formatedSize || `${pkg.size}g`}`;
  };

  const getSourceBatchById = (ref: string): FreshProductBatch | undefined => {
    return freshBatches.find((fb) => fb.firebaseId === ref);
  };

  const getAllowedSourceProductIds = (productId: string): string[] => {
    const product = parents.find((parent) => parent.id === productId);
    return [productId, ...(product?.componentProductIds || [])];
  };

  const getFreshBatchesForParent = (parentId: string, includeBatchRef?: string): FreshProductBatch[] => {
    const allowedProductIds = getAllowedSourceProductIds(parentId);
    return freshBatches.filter((fb) =>
      allowedProductIds.includes(fb.productId) &&
      (fb.firebaseId === includeBatchRef || getFreshBatchStatus({ plantDate: fb.plantDate, harvestDate: fb.harvestDate, canceled: fb.canceled, exception: fb.exception }) !== "historic")
    );
  };

  const isSourceBatchAllowed = (productId: string, sourceBatch: FreshProductBatch): boolean =>
    getAllowedSourceProductIds(productId).includes(sourceBatch.productId);

  const getSourceProductName = (sourceBatch: FreshProductBatch): string =>
    parents.find((parent) => parent.id === sourceBatch.productId)?.name_sv || sourceBatch.productId;

  const isPackageActive = (pkg: FreshPackage): boolean => {
    if (pkg.active === false) return false;
    if (!pkg.boundBagPackageId) return true;
    return packages.find((candidate) => candidate.id === pkg.boundBagPackageId)?.active !== false;
  };

  const getAvailablePackages = (
    productId: string,
    sourceBatchRef: string,
    editingEntryId?: string,
    excludedPackageIds: string[] = [],
  ): FreshPackage[] => {
    return packages.filter((pkg) => {
      const isCurrentPackage = editingEntryId && packageBatches.find((entry) => entry.firebaseId === editingEntryId)?.packageId === pkg.id;
      if (pkg.parentId !== productId || pkg.isBox || (!isPackageActive(pkg) && !isCurrentPackage) || excludedPackageIds.includes(pkg.id)) return false;
      return !packageBatches.some(
        (entry) =>
          entry.packageId === pkg.id &&
          entry.sourceBatchRef === sourceBatchRef &&
          entry.firebaseId !== editingEntryId,
      );
    });
  };

  const getCommonAvailablePackages = (productId: string, sourceBatches: FreshProductBatch[], editingEntryId?: string): FreshPackage[] => {
    if (sourceBatches.length === 0) return [];
    return packages.filter((pkg) =>
      !pkg.isBox &&
      pkg.parentId === productId &&
      sourceBatches.some((sourceBatch) => getAvailablePackages(productId, sourceBatch.firebaseId, editingEntryId).some((availablePackage) => availablePackage.id === pkg.id))
    );
  };

  const getAvailableSourceBatchesForPackage = (
    productId: string,
    sourceBatches: FreshProductBatch[],
    packageId: string,
    editingEntryId?: string,
  ): FreshProductBatch[] => sourceBatches.filter((sourceBatch) =>
    getAvailablePackages(productId, sourceBatch.firebaseId, editingEntryId).some((availablePackage) => availablePackage.id === packageId)
  );

  const getBoxPackageInfo = (bagPackage: FreshPackage): { boxPackage: FreshPackage; multiplier: number } | null => {
    if (bagPackage.isBox) return null;
    const productBoxes = packages.filter((pkg) => pkg.parentId === bagPackage.parentId && pkg.isBox);
    const matchingBox = productBoxes.find((boxPackage) => {
      const sizeMatch = boxPackage.formatedSize.match(/(\d+)\s*x\s*([\d,.]+)\s*g/i);
      const labelMultiplier = sizeMatch ? Number(sizeMatch[1]) : 0;
      const labelBagSize = sizeMatch ? Number(sizeMatch[2].replace(",", ".")) : 0;
      if (labelMultiplier > 0 && labelBagSize === bagPackage.size) return true;
      return bagPackage.size > 0 && boxPackage.size > 0 && boxPackage.size / bagPackage.size % 1 === 0;
    });
    if (!matchingBox) return null;
    const sizeMatch = matchingBox.formatedSize.match(/(\d+)\s*x\s*([\d,.]+)\s*g/i);
    const labelMultiplier = sizeMatch ? Number(sizeMatch[1]) : 0;
    const multiplier = labelMultiplier || matchingBox.size / bagPackage.size;
    return multiplier > 1 ? { boxPackage: matchingBox, multiplier } : null;
  };

  const getAutomaticBoxData = (bagPackage: FreshPackage, quantity: number) => {
    const boxInfo = getBoxPackageInfo(bagPackage);
    return boxInfo
      ? { boxPackageId: boxInfo.boxPackage.id, boxQuantity: Math.floor(quantity / boxInfo.multiplier), boxMultiplier: boxInfo.multiplier }
      : { boxPackageId: "", boxQuantity: 0, boxMultiplier: 0 };
  };

  const getSortedEntries = (): PackageBatch[] => {
    const sorted = [...packageBatches];
    if (sortBy === "package") {
      sorted.sort((a, b) => getPackageLabel(a.packageId).localeCompare(getPackageLabel(b.packageId), "sv-SE"));
    } else if (sortBy === "batch") {
      sorted.sort((a, b) => {
        const sbA = getSourceBatchById(a.sourceBatchRef);
        const sbB = getSourceBatchById(b.sourceBatchRef);
        const dateA = sbA?.harvestDate || sbA?.plantDate || 0;
        const dateB = sbB?.harvestDate || sbB?.plantDate || 0;
        return dateB - dateA;
      });
    } else {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return sorted;
  };

  const isThisWeek = (entry: PackageBatch) => {
    const harvestDate = getSourceBatchById(entry.sourceBatchRef)?.harvestDate;
    return !!harvestDate && moment(harvestDate).isBetween(moment().startOf("isoWeek"), moment().endOf("isoWeek"), undefined, "[]");
  };

  const matchesFilter = (entry: PackageBatch, filter: StockFilter) => {
    if (filter === "week") return isThisWeek(entry);
    if (filter === "available") return availableOf(entry) > 0;
    if (filter === "reserved") return reservedOf(entry) > 0;
    if (filter === "unavailable") return availableOf(entry) === 0;
    return true;
  };

  const searchTerm = search.trim().toLocaleLowerCase("sv-SE");
  const matchesSearch = (entry: PackageBatch) => {
    const source = getSourceBatchById(entry.sourceBatchRef);
    return [getPackageLabel(entry.packageId), entry.batchId, entry.sourceBatchId,
      source && getSourceProductName(source), source?.plantedBy, source?.harvestedBy,
    ].some((value) => value?.toLocaleLowerCase("sv-SE").includes(searchTerm));
  };

  const clearFilters = () => { setSearch(""); setStockFilter("all"); };
  const upsertStock = (entry: PackageBatch) => setPackageBatches((items) => [
    { ...items.find((item) => item.firebaseId === entry.firebaseId), ...entry },
    ...items.filter((item) => item.firebaseId !== entry.firebaseId),
  ]);
  const sourceBatchLabel = (batch: FreshProductBatch) => `${batch.batchId} · ${getSourceProductName(batch)}`;

  // --- Single edit helpers ---

  const resetSingleForm = () => {
    setSingleError("");
    setSingleProduct(null);
    setSinglePackage(null);
    setSingleSourceBatches([]);
    setSingleQuantity(1);
    setEditingEntry(null);
  };

  const handleOpenAdd = () => { resetSingleForm(); setOpen(true); };

  const handleOpenEdit = (entry: PackageBatch) => {
    setSingleError("");
    setEditingEntry(entry);
    const pkg = packages.find((p) => p.id === entry.packageId);
    const product = pkg ? parents.find((parent) => parent.id === pkg.parentId) : undefined;
    setSingleProduct(product || null);
    setSinglePackage(pkg || null);
    const sb = getSourceBatchById(entry.sourceBatchRef);
    setSingleSourceBatches(sb ? [sb] : []);
    setSingleQuantity(quantityOf(entry));
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDeleteEntry = async () => {
    if (!deletingEntry) return;
    const current = packageBatches.find((entry) => entry.firebaseId === deletingEntry.firebaseId) || deletingEntry;
    if (reservedOf(current) > 0) {
      setDeleteError("Posten har reserverade paket. Ta bort reservationerna i Veckobeställningar först.");
      return;
    }
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await deleteDoc(doc(firebaseStore, "products_fresh_package_batch", current.firebaseId));
      setPackageBatches((entries) => entries.filter((entry) => entry.firebaseId !== current.firebaseId));
      setDeleteOpen(false);
    } catch (error) {
      console.error("Error deleting stock:", error);
      setDeleteError("Lagerposten kunde inte tas bort. Försök igen.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveSingle = async () => {
    if (!singleProduct) { setSingleError("Välj en färsk produkt"); return; }
    if (singleSourceBatches.length === 0) { setSingleError("Välj minst en batch"); return; }
    if (!singlePackage) { setSingleError("Välj en förpackning"); return; }
    if (editingEntry && singleSourceBatches.length !== 1) { setSingleError("En redigerad lagerpost kan bara ha en batch"); return; }
    if (!Number.isInteger(singleQuantity) || singleQuantity < 1) {
      setSingleError("Ange ett antal på minst 1");
      return;
    }
    const currentEntry = packageBatches.find((entry) => entry.firebaseId === editingEntry?.firebaseId) || editingEntry;
    if (currentEntry && singleQuantity < reservedOf(currentEntry)) {
      setSingleError(`Antalet kan inte vara mindre än reserverat antal (${reservedOf(currentEntry)})`);
      return;
    }
    if (singleSourceBatches.some((sourceBatch) => !isSourceBatchAllowed(singleProduct.id, sourceBatch)) || singlePackage.parentId !== singleProduct.id) {
      setSingleError("Produkt, batch och förpackning måste höra ihop");
      return;
    }
    const availableSourceBatches = getAvailableSourceBatchesForPackage(singleProduct.id, singleSourceBatches, singlePackage.id, editingEntry?.firebaseId);
    if (availableSourceBatches.length === 0) {
      setSingleError("Förpackningen finns redan i lagret för alla valda batcher");
      return;
    }

    setSingleError("");
    setDialogLoading(true);

    const batchDocs = availableSourceBatches.map((sourceBatch) => {
      return {
        packageId: singlePackage.id,
        sourceBatchRef: sourceBatch.firebaseId,
        sourceBatchId: sourceBatch.batchId,
        sourceProductId: sourceBatch.productId,
        batchId: sourceBatch.batchId,
        quantity: singleQuantity,
        ...getAutomaticBoxData(singlePackage, singleQuantity),
      };
    });

    try {
      if (editingEntry) {
        await updateDoc(doc(firebaseStore, "products_fresh_package_batch", editingEntry.firebaseId), batchDocs[0]);
        setPackageBatches((entries) => entries.map((entry) => entry.firebaseId === editingEntry.firebaseId ? { ...entry, ...batchDocs[0] } : entry));
      } else {
        const collectionRef = collection(firebaseStore, "products_fresh_package_batch");
        for (const batchData of batchDocs) {
          const data = { ...batchData, createdAt: Date.now() };
          const created = await addDoc(collectionRef, data);
          upsertStock({ ...data, firebaseId: created.id });
          setSingleSourceBatches((sources) => sources.filter((source) => source.firebaseId !== batchData.sourceBatchRef));
        }
      }
      clearFilters();
      handleClose();
    } catch (error) {
      console.error("Error saving entry:", error);
      setSingleError(editingEntry ? "Lagerposten kunde inte sparas. Dina uppgifter finns kvar, försök igen." : "Alla poster kunde inte skapas. Redan sparade batcher har tagits bort från urvalet. Försök igen med de återstående.");
    }
    setDialogLoading(false);
  };

  // --- Multi-batch helpers ---

  const createMultiRow = (): MultiStockRow => ({
    id: `${Date.now()}-${Math.random()}`,
    product: null,
    sourceBatch: null,
    package: null,
    quantity: 1,
  });

  const resetMultiForm = () => {
    setMultiError("");
    setMultiRows([createMultiRow()]);
  };

  const handleOpenMultiAdd = () => { resetMultiForm(); setMultiOpen(true); };

  const handleCloseMulti = () => setMultiOpen(false);

  const updateMultiRow = (rowId: string, changes: Partial<MultiStockRow>) => {
    setMultiRows((rows) => rows.map((row) => (row.id === rowId ? { ...row, ...changes } : row)));
  };

  const getMultiRowPackages = (row: MultiStockRow): FreshPackage[] => {
    if (!row.product || !row.sourceBatch) return [];
    const excludedPackageIds = multiRows
      .filter((otherRow) => otherRow.id !== row.id && otherRow.sourceBatch?.firebaseId === row.sourceBatch?.firebaseId)
      .map((otherRow) => otherRow.package?.id)
      .filter((packageId): packageId is string => Boolean(packageId));
    return getAvailablePackages(row.product.id, row.sourceBatch.firebaseId, undefined, excludedPackageIds);
  };

  const handleSaveMulti = async () => {
    if (multiRows.length === 0 || multiRows.some((row) => !row.product || !row.sourceBatch || !row.package)) {
      setMultiError("Välj produkt, batch och förpackning för varje rad");
      return;
    }
    if (multiRows.some((row) => !Number.isInteger(row.quantity) || row.quantity < 1)) {
      setMultiError("Ange ett antal på minst 1 för varje rad");
      return;
    }
    if (multiRows.some((row) => !isSourceBatchAllowed(row.product!.id, row.sourceBatch!) || row.package!.parentId !== row.product!.id)) {
      setMultiError("Produkt, batch och förpackning måste höra ihop på varje rad");
      return;
    }
    const rowKeys = multiRows.map((row) => `${row.sourceBatch!.firebaseId}:${row.package!.id}`);
    if (new Set(rowKeys).size !== rowKeys.length) {
      setMultiError("Samma förpackning kan inte läggas till två gånger för samma batch");
      return;
    }
    if (multiRows.some((row) => !getAvailablePackages(row.product!.id, row.sourceBatch!.firebaseId).some((pkg) => pkg.id === row.package!.id))) {
      setMultiError("En eller flera förpackningar finns redan i lagret för vald batch");
      return;
    }

    setMultiError("");
    setMultiDialogLoading(true);

    const batchDocs = multiRows.map((row) => ({
      packageId: row.package!.id,
      sourceBatchRef: row.sourceBatch!.firebaseId,
      sourceBatchId: row.sourceBatch!.batchId,
      sourceProductId: row.sourceBatch!.productId,
      batchId: row.sourceBatch!.batchId,
      quantity: row.quantity,
      ...getAutomaticBoxData(row.package!, row.quantity),
      createdAt: Date.now(),
    }));

    try {
      const collectionRef = collection(firebaseStore, "products_fresh_package_batch");
      for (const docData of batchDocs) {
        const created = await addDoc(collectionRef, docData);
        upsertStock({ ...docData, firebaseId: created.id });
        setMultiRows((rows) => rows.filter((row) => !(row.sourceBatch?.firebaseId === docData.sourceBatchRef && row.package?.id === docData.packageId)));
      }
      clearFilters();
      handleCloseMulti();
    } catch (error) {
      console.error("Error saving multi-batch:", error);
      setMultiError("Alla poster kunde inte skapas. Bara återstående rader visas. Försök igen.");
    }
    setMultiDialogLoading(false);
  };

  const hasLoadError = Object.values(loadErrors).some(Boolean);
  const searchedEntries = getSortedEntries().filter(matchesSearch);
  const visibleEntries = searchedEntries.filter((entry) => matchesFilter(entry, stockFilter));
  const hasFilters = !!searchTerm || stockFilter !== "all";
  const currentEditingEntry = packageBatches.find((entry) => entry.firebaseId === editingEntry?.firebaseId) || editingEntry;
  const currentDeletingEntry = packageBatches.find((entry) => entry.firebaseId === deletingEntry?.firebaseId) || deletingEntry;
  const reservedMinimum = currentEditingEntry ? reservedOf(currentEditingEntry) : 0;
  const singleEligibleSources = singleProduct && singlePackage ? getAvailableSourceBatchesForPackage(singleProduct.id, singleSourceBatches, singlePackage.id, editingEntry?.firebaseId) : [];
  const singlePackages = singleProduct ? getCommonAvailablePackages(singleProduct.id, singleSourceBatches, editingEntry?.firebaseId) : [];
  const quantityValid = Number.isInteger(singleQuantity) && singleQuantity >= Math.max(1, reservedMinimum);
  const singleBoxes = singlePackage ? getBoxPackageInfo(singlePackage) : null;
  const retryLoad = () => {
    setLoadErrors({});
    setLoadingCollections({ products: true, packages: true, batches: true, stock: true });
    setReload((value) => value + 1);
  };

  return (
    <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace StockWorkspace">
      <div className="FreshTabToolbar">
        <div><p className="FreshEyebrow">Lager</p><h2 className="FreshTabTitle">Färskt lager</h2><p className="FreshTabSummary">Färdigpackat, reserverat och ledigt – samlat per odlingsbatch.</p></div>
        <div className="StockPrimaryActions">
          <IconButton title="Om Färskt lager" aria-label="Om Färskt lager" onClick={() => setInfoDialogOpen(true)}><InfoOutlinedIcon /></IconButton>
          <Button color="inherit" variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={firebaseLoading || hasLoadError}>Ny lagerpost</Button>
        </div>
      </div>

      {hasLoadError && <Alert severity="error" className="StockLoadError" action={<Button color="inherit" onClick={retryLoad}>Försök igen</Button>}>{Object.values(loadErrors).filter(Boolean).join(" ")}</Alert>}
      <section className="StockOverview" aria-label="Översikt för hela lagret">
        {[
          { label: "Paket totalt", value: packageBatches.reduce((sum, entry) => sum + quantityOf(entry), 0), hint: "Hela lagret", Icon: Inventory2OutlinedIcon, className: "" },
          { label: "Reserverade", value: packageBatches.reduce((sum, entry) => sum + reservedOf(entry), 0), hint: "Bokade till kundorder", Icon: LockOutlinedIcon, className: "StockOverviewReserved" },
          { label: "Lediga paket", value: packageBatches.reduce((sum, entry) => sum + availableOf(entry), 0), hint: "Totalt minus reserverat per post", Icon: CheckCircleOutlineIcon, className: "StockOverviewAvailable" },
        ].map(({ label, value, hint, Icon, className }) => <div className={`StockOverviewItem ${className}`} key={label}><div><span>{label}</span><strong>{firebaseLoading || hasLoadError ? "—" : value.toLocaleString("sv-SE")}</strong><small>{hint}</small></div><Icon /></div>)}
      </section>

      <div className="StockSecondaryActions"><Button color="inherit" variant="outlined" startIcon={<PlaylistAddIcon />} onClick={handleOpenMultiAdd} disabled={firebaseLoading || hasLoadError}>Flera lagerposter</Button><span>{firebaseLoading ? "Hämtar lageruppgifter…" : hasLoadError ? "Lageruppgifterna kunde inte hämtas" : `${stockCountLabel(packageBatches.length)} registrerade`}</span></div>

      <div className="BatchTools" role="search" aria-label="Filtrera färskt lager">
        <div className="BatchSearchRow">
          <TextField className="BatchSearch" size="small" label="Sök i lagret" placeholder="Produkt, förpackning, batch-ID…" value={search} onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, endAdornment: search ? <InputAdornment position="end"><IconButton size="small" aria-label="Rensa sökning" onClick={() => setSearch("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : undefined }} />
          <TextField className="BatchSort" size="small" select label="Sortera" value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)}>
            <MenuItem value="created">Senast skapad</MenuItem><MenuItem value="package">Förpackning A–Ö</MenuItem><MenuItem value="batch">Skörd / sådd, senast först</MenuItem>
          </TextField>
        </div>
        <div className="BatchFilterRow"><div className="BatchFilters" role="group" aria-label="Filtrera lagerposter">
          {STOCK_FILTERS.map((filter) => <button type="button" key={filter.value} className={`BatchFilter StockFilter-${filter.value}`} aria-pressed={stockFilter === filter.value} onClick={() => setStockFilter(filter.value)}>{filter.label}<span className="BatchFilterCount">{firebaseLoading || hasLoadError ? "—" : searchedEntries.filter((entry) => matchesFilter(entry, filter.value)).length}</span></button>)}
        </div>{hasFilters && <Button color="inherit" size="small" onClick={clearFilters}>Rensa filter</Button>}</div>
        <p className="BatchScope">Veckans skörd: {moment().startOf("isoWeek").format("YYYY-MM-DD")}–{moment().endOf("isoWeek").format("YYYY-MM-DD")}. Filtren visar lagerposter; översikten gäller hela lagret.</p>
      </div>

      {firebaseLoading ? <div className="BatchEmpty" role="status"><CircularProgress color="inherit" size={24} /><p>Hämtar lager och batchuppgifter…</p></div> : !hasLoadError && (
        <section className="BatchProcess StockList" aria-labelledby="stock-list-title">
          <div className="StockListHeader"><h3 id="stock-list-title">{STOCK_FILTERS.find((filter) => filter.value === stockFilter)?.label === "Alla" ? "Alla lagerposter" : STOCK_FILTERS.find((filter) => filter.value === stockFilter)?.label}</h3><span role="status">{visibleEntries.length} av {stockCountLabel(packageBatches.length)}</span></div>
          {visibleEntries.length ? <div className="BatchGrid StockCardGrid">{visibleEntries.map((entry) => {
            const pkg = packages.find((item) => item.id === entry.packageId);
            const product = parents.find((parent) => parent.id === pkg?.parentId);
            const source = getSourceBatchById(entry.sourceBatchRef);
            const available = availableOf(entry);
            const reserved = reservedOf(entry);
            return <article className="BatchCard StockCard" key={entry.firebaseId} aria-label={`${getPackageLabel(entry.packageId)}, ${entry.batchId}`}>
              <div className="BatchCardHeader"><div className="BatchProductImage"><Inventory2OutlinedIcon /></div><div className="BatchIdentity"><h4>{product?.name_sv || pkg?.parentId || "Okänd produkt"}</h4><span className="StockPackageSize">{pkg?.formatedSize || (pkg ? `${pkg.size} g` : entry.packageId)}</span></div><IconButton color="inherit" title="Redigera lagerpost" aria-label={`Redigera ${entry.batchId}, ${getPackageLabel(entry.packageId)}`} onClick={() => handleOpenEdit(entry)}><EditIcon fontSize="small" /></IconButton></div>
              <div className="StockCardBatch"><span className="BatchId">Batch {entry.batchId}</span>{isThisWeek(entry) && <span className="StockWeekBadge">Veckans skörd</span>}</div>
              <dl className="StockQuantities"><div><dt>Totalt</dt><dd>{quantityOf(entry).toLocaleString("sv-SE")}</dd></div><div><dt>Reserverat</dt><dd>{reserved.toLocaleString("sv-SE")}</dd></div><div className={available > 0 ? "StockAvailable" : "StockUnavailable"}><dt>Ledigt</dt><dd>{available.toLocaleString("sv-SE")}<small>paket</small></dd></div></dl>
              {reserved > quantityOf(entry) && <p className="StockNotice">Reservationerna överstiger antalet paket. Kontrollera lagerposten.</p>}
              <dl className="BatchDates"><div><dt>Sådd</dt><dd>{source?.plantDate ? moment(source.plantDate).format("YYYY-MM-DD") : "Ej angiven"}</dd></div><div><dt>Skörd</dt><dd>{source?.harvestDate ? moment(source.harvestDate).format("YYYY-MM-DD") : "Ej angiven"}</dd></div></dl>
              <div className="StockOrigin"><span>Odlad som</span><strong>{source ? getSourceProductName(source) : parents.find((parent) => parent.id === entry.sourceProductId)?.name_sv || "Batchuppgift saknas"}</strong></div>
              <div className="BatchCardFooter"><span className="StockBoxSummary">{entry.boxQuantity ? `Totalt motsvarande ${entry.boxQuantity} ${entry.boxQuantity === 1 ? "hel box" : "hela boxar"}${entry.boxMultiplier ? ` à ${entry.boxMultiplier} paket` : ""}` : reserved > 0 ? "Paket reserverade till kundorder" : "Inga reservationer"}</span><IconButton color="inherit" className="BatchDelete" title={reserved > 0 ? "Visa varför lagerposten inte kan tas bort" : "Ta bort lagerpost"} aria-label={`Ta bort ${entry.batchId}, ${getPackageLabel(entry.packageId)}`} onClick={() => { setDeletingEntry(entry); setDeleteError(""); setDeleteOpen(true); }}>{reserved > 0 ? <LockOutlinedIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}</IconButton></div>
            </article>;
          })}</div> : <div className="BatchEmpty" role="status"><Inventory2OutlinedIcon /><strong>{packageBatches.length ? "Inga lagerposter matchar ditt urval" : "Inga lagerposter ännu"}</strong><p>{packageBatches.length ? "Prova en annan sökning eller rensa filtren." : "Koppla färdigpackade produkter till deras odlingsbatcher för att komma igång."}</p>{hasFilters ? <Button color="inherit" onClick={clearFilters}>Rensa filter</Button> : <Button color="inherit" variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>Ny lagerpost</Button>}</div>}
        </section>
      )}

      <StockDialog id="stock-edit" open={open} onClose={handleClose} title={editingEntry ? "Redigera lagerpost" : "Ny lagerpost"}
        description={editingEntry ? `${getPackageLabel(editingEntry.packageId)} · ${editingEntry.batchId}` : "Välj produkt, odlingsbatcher och förpackning. Antalet gäller per batch."}
        busy={dialogLoading} error={singleError} wide
        actions={<><Button color="inherit" onClick={handleClose} disabled={dialogLoading}>Avbryt</Button><Button color="inherit" variant="contained" onClick={handleSaveSingle} disabled={dialogLoading || !singleProduct || !singlePackage || !singleEligibleSources.length || !quantityValid}>{dialogLoading ? "Sparar…" : editingEntry ? "Spara lagerpost" : `Skapa ${stockCountLabel(singleEligibleSources.length)}`}</Button></>}>
        <div className="StockForm">
          {reservedMinimum > 0 && <Alert severity="info">{reservedMinimum} paket är reserverade till kundorder. Totalantalet måste vara minst {reservedMinimum} paket. Reservationer hanteras i Veckobeställningar.</Alert>}
          <StockFormSection title="Produkt och odlingsbatch">
            <div className="StockFormFields">
              <Autocomplete options={parents} getOptionLabel={(product) => `${product.name_sv}${product.name_en ? ` / ${product.name_en}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id} value={singleProduct}
                onChange={(_event, value) => { setSingleProduct(value); setSingleSourceBatches([]); setSinglePackage(null); }} disabled={dialogLoading} noOptionsText="Inga produkter hittades"
                renderInput={(params) => <TextField {...params} label="Produkt *" size="small" fullWidth helperText="Den färdiga produkten som ska säljas." />} />
              <Autocomplete multiple={!editingEntry} options={singleProduct ? getFreshBatchesForParent(singleProduct.id, editingEntry?.sourceBatchRef) : []}
                getOptionLabel={sourceBatchLabel} isOptionEqualToValue={(a, b) => a.firebaseId === b.firebaseId}
                value={editingEntry ? singleSourceBatches[0] || null : singleSourceBatches}
                onChange={(_event, value) => { setSingleSourceBatches(Array.isArray(value) ? value : value ? [value] : []); setSinglePackage(null); }}
                renderTags={(values, getTagProps) => values.map((batch, index) => <Chip {...getTagProps({ index })} label={batch.batchId} size="small" />)}
                renderOption={(props, batch) => <li {...props} key={batch.firebaseId}><div className="StockBatchOption"><strong>{batch.batchId}</strong><span>{getSourceProductName(batch)} · {batch.harvestDate ? `Skörd ${moment(batch.harvestDate).format("YYYY-MM-DD")}` : "Skörd ej angiven"}</span></div></li>}
                disabled={dialogLoading || !singleProduct} noOptionsText="Inga valbara batcher för produkten"
                renderInput={(params) => <TextField {...params} label={editingEntry ? "Odlingsbatch *" : "Odlingsbatcher *"} size="small" fullWidth helperText={!singleProduct ? "Välj en produkt först." : editingEntry ? "En lagerpost hör till en odlingsbatch." : "Välj en eller flera batcher. För mixprodukter visas även komponentbatcher."} />} />
            </div>
          </StockFormSection>
          <StockFormSection title="Förpackning och antal">
            <div className="StockFormGrid">
              <Autocomplete options={singlePackages} getOptionLabel={(pkg) => pkg.formatedSize || `${pkg.size} g`} isOptionEqualToValue={(a, b) => a.id === b.id} value={singlePackage} onChange={(_event, value) => setSinglePackage(value)}
                disabled={dialogLoading || !singleSourceBatches.length} noOptionsText="Ingen ledig förpackning för valda batcher"
                renderInput={(params) => <TextField {...params} label="Förpackning *" size="small" fullWidth helperText={!singleSourceBatches.length ? "Välj en odlingsbatch först." : !singlePackages.length ? "Aktiva påsförpackningar saknas eller finns redan i lagret för valda batcher." : "Boxar räknas automatiskt från antalet paket."} />} />
              <TextField label={editingEntry ? "Totalt antal paket *" : "Paket per batch *"} type="number" size="small" fullWidth value={singleQuantity} onChange={(event) => setSingleQuantity(event.target.value ? Number(event.target.value) : 0)} disabled={dialogLoading || !singlePackage}
                inputProps={{ min: Math.max(1, reservedMinimum), step: 1 }} error={!!singlePackage && !quantityValid} helperText={singlePackage && !quantityValid ? `Ange ett heltal på minst ${Math.max(1, reservedMinimum)}.` : editingEntry ? "Totalantal inklusive reserverade paket." : "Samma antal registreras för varje ledig batch."} />
            </div>
          </StockFormSection>
          {singlePackage && singleSourceBatches.length > 0 && <section className="StockSavePreview" aria-label="Sammanfattning av lagerposter">
            <div className="StockPreviewHeading"><strong>{editingEntry ? "Efter ändringen" : "Det här registreras"}</strong><span>{stockCountLabel(singleEligibleSources.length)}</span></div>
            {singleSourceBatches.map((source) => {
              const eligible = singleEligibleSources.some((item) => item.firebaseId === source.firebaseId);
              return <div className={`StockPreviewRow${eligible ? "" : " isSkipped"}`} key={source.firebaseId}><div><strong>{source.batchId}</strong><span>{getSourceProductName(source)}</span></div><span>{eligible ? `${singleQuantity} paket` : "Redan i lager – hoppas över"}</span></div>;
            })}
            <div className="StockPreviewTotal"><span>{editingEntry ? `${Math.max(0, singleQuantity - reservedMinimum)} lediga efter reservationer` : `${singleEligibleSources.length * singleQuantity} paket totalt`}</span>{singleBoxes && quantityValid && <small>{Math.floor(singleQuantity / singleBoxes.multiplier)} hela boxar à {singleBoxes.multiplier} paket per batch</small>}</div>
          </section>}
        </div>
      </StockDialog>

      <StockDialog id="stock-multi" open={multiOpen} onClose={handleCloseMulti} title="Skapa flera lagerposter"
        description="Varje rad kopplar en produktförpackning till en odlingsbatch. Välj antal paket separat för varje rad."
        busy={multiDialogLoading} error={multiError} wide
        actions={<><Button color="inherit" onClick={handleCloseMulti} disabled={multiDialogLoading}>Avbryt</Button><Button color="inherit" variant="contained" onClick={handleSaveMulti} disabled={multiDialogLoading || !multiRows.length || multiRows.some((row) => !row.product || !row.sourceBatch || !row.package || !Number.isInteger(row.quantity) || row.quantity < 1)}>{multiDialogLoading ? "Skapar lagerposter…" : `Skapa ${stockCountLabel(multiRows.length)}`}</Button></>}>
        <div className="StockForm">
          <div className="StockMultiSummary" role="status"><strong>{stockCountLabel(multiRows.length)}</strong><span>{multiRows.reduce((sum, row) => sum + (row.package && row.quantity > 0 ? row.quantity : 0), 0)} paket valda</span></div>
          {multiRows.map((row, index) => {
            const options = getMultiRowPackages(row);
            const boxInfo = row.package ? getBoxPackageInfo(row.package) : null;
            return <section className="StockMultiRow" key={row.id} aria-label={`Lagerpost ${index + 1}`}>
              <div className="StockMultiRowHeader"><h3>Lagerpost {index + 1}</h3><IconButton color="inherit" aria-label={`Ta bort rad ${index + 1}`} title="Ta bort rad" onClick={() => setMultiRows((rows) => rows.filter((item) => item.id !== row.id))} disabled={multiDialogLoading || multiRows.length === 1}><CloseIcon fontSize="small" /></IconButton></div>
              <div className="StockFormGrid">
                <Autocomplete options={parents} getOptionLabel={(product) => product.name_sv} isOptionEqualToValue={(a, b) => a.id === b.id} value={row.product}
                  onChange={(_event, value) => updateMultiRow(row.id, { product: value, sourceBatch: null, package: null })} disabled={multiDialogLoading} noOptionsText="Inga produkter hittades"
                  renderInput={(params) => <TextField {...params} label="Produkt *" size="small" fullWidth />} />
                <Autocomplete options={row.product ? getFreshBatchesForParent(row.product.id) : []} getOptionLabel={sourceBatchLabel} isOptionEqualToValue={(a, b) => a.firebaseId === b.firebaseId} value={row.sourceBatch}
                  onChange={(_event, value) => updateMultiRow(row.id, { sourceBatch: value, package: null })} disabled={multiDialogLoading || !row.product} noOptionsText="Inga valbara batcher för produkten"
                  renderInput={(params) => <TextField {...params} label="Odlingsbatch *" size="small" fullWidth helperText={!row.product ? "Välj produkt först." : undefined} />} />
                <Autocomplete options={options} getOptionLabel={(pkg) => pkg.formatedSize || `${pkg.size} g`} isOptionEqualToValue={(a, b) => a.id === b.id} value={row.package}
                  onChange={(_event, value) => updateMultiRow(row.id, { package: value })} disabled={multiDialogLoading || !row.sourceBatch} noOptionsText="Ingen ledig förpackning för den här batchen"
                  renderInput={(params) => <TextField {...params} label="Förpackning *" size="small" fullWidth helperText={!row.sourceBatch ? "Välj batch först." : !options.length ? "Förpackningar saknas eller används redan i lagret eller på en annan rad." : undefined} />} />
                <TextField label="Antal paket *" type="number" size="small" fullWidth value={row.quantity} onChange={(event) => updateMultiRow(row.id, { quantity: event.target.value ? Number(event.target.value) : 0 })}
                  disabled={multiDialogLoading || !row.package} inputProps={{ min: 1, step: 1 }} error={!!row.package && (!Number.isInteger(row.quantity) || row.quantity < 1)} helperText={row.package && (!Number.isInteger(row.quantity) || row.quantity < 1) ? "Ange ett heltal på minst 1." : undefined} />
              </div>
              {boxInfo && row.quantity > 0 && <p className="StockHelpText">Motsvarar {Math.floor(row.quantity / boxInfo.multiplier)} hela boxar à {boxInfo.multiplier} paket.</p>}
            </section>;
          })}
          <Button className="StockAddRow" color="inherit" variant="outlined" startIcon={<AddIcon />} onClick={() => setMultiRows((rows) => [...rows, createMultiRow()])} disabled={multiDialogLoading}>Lägg till en rad</Button>
        </div>
      </StockDialog>

      <StockDialog id="stock-delete" open={deleteOpen} onClose={() => setDeleteOpen(false)} title={currentDeletingEntry && reservedOf(currentDeletingEntry) > 0 ? "Lagerposten har reservationer" : "Ta bort lagerpost?"}
        description={currentDeletingEntry && reservedOf(currentDeletingEntry) > 0 ? "Ta bort reservationerna i Veckobeställningar innan du tar bort lagerposten." : "Lagerposten tas bort permanent. Odlingsbatchen behålls."}
        busy={deleteLoading} error={deleteError}
        actions={<><Button color="inherit" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Stäng</Button><Button className="StockDeleteButton" color="inherit" variant="contained" onClick={handleDeleteEntry} disabled={deleteLoading || !currentDeletingEntry || reservedOf(currentDeletingEntry) > 0}>{deleteLoading ? "Tar bort…" : "Ta bort lagerpost"}</Button></>}>
        {currentDeletingEntry && <div className="StockDeleteSummary"><Inventory2OutlinedIcon /><div><strong>{getPackageLabel(currentDeletingEntry.packageId)}</strong><span>{currentDeletingEntry.batchId}</span><small>{quantityOf(currentDeletingEntry)} paket · {reservedOf(currentDeletingEntry)} reserverade</small></div></div>}
      </StockDialog>

      <StockDialog id="stock-info" open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} title="Om Färskt lager" description="Lagerposter knyter ihop färdigpackade produkter, odlingsbatcher och antal paket."
        actions={<Button color="inherit" variant="contained" onClick={() => setInfoDialogOpen(false)}>Stäng</Button>}>
        <div className="StockForm">
          <StockFormSection title="Registrera färdigpackat lager"><ol className="StockHelpSteps"><li><strong>Välj produkt och batcher</strong><span>Ny lagerpost kan skapa en post per vald batch. För mixprodukter visas även batcher från länkade komponentprodukter.</span></li><li><strong>Välj förpackning och antal</strong><span>Aktiva påsförpackningar visas. Kombinationer som redan finns i lagret hoppas över och visas i sammanfattningen.</span></li><li><strong>Kontrollera och spara</strong><span>Antalet gäller per batch. Använd Flera lagerposter om raderna ska ha olika produkter, förpackningar eller antal.</span></li></ol></StockFormSection>
          <StockFormSection title="Läs av lager och reservationer"><dl className="StockHelpDefinitions"><div><dt>Totalt</dt><dd>Alla registrerade paket, inklusive reserverade.</dd></div><div><dt>Reserverat</dt><dd>Paket bokade till kundorder i Veckobeställningar.</dd></div><div><dt>Ledigt</dt><dd>Antalet paket som återstår efter reservationer, lägst noll per post.</dd></div><div><dt>Boxar</dt><dd>Beräknas automatiskt från totalt antal paket när en matchande boxförpackning finns. Boxarna är inte extra lager.</dd></div></dl></StockFormSection>
          <Alert severity="info">Veckans skörd filtrerar på odlingsbatchens skördedatum, även kommande skörd denna vecka. Historiska batcher erbjuds inte som nya val, men befintliga lagerposter finns kvar.</Alert>
        </div>
      </StockDialog>
    </div>
  );
}

export default DashBoardFreshStock;
